import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { finalize, pluck, tap } from 'rxjs/operators';
import { NewRestaurantWorkPeriod, RestaurantWorkPeriod } from '../../interfaces/period';
import { AuthService } from '../../services/auth/auth.service';
import { RestaurantService } from '../../services/restaurant/restaurant.service';

@UntilDestroy()
@Component({
  selector: 'app-business-hours-page',
  templateUrl: './business-hours-page.component.html',
  styleUrls: ['./business-hours-page.component.scss']
})
export class BusinessHoursPageComponent implements OnInit {

  public restaurantId!: number;
  public workingHours: RestaurantWorkPeriod[] = [];
  public editable = false;
  public isEditPeriods = false;
  public loading = true;

  constructor(
    private activateRoute: ActivatedRoute,
    private notifier: NotifierService,
    private translate: TranslateService,
    private restaurant: RestaurantService,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.activateRoute.parent?.data.pipe(
      pluck('restaurant'),
      tap(restaurant => {
        this.editable = !this.auth.isViewer(restaurant);
        this.restaurantId = restaurant.id;
        this.getWorkingHours();
      }),
      untilDestroyed(this),
    ).subscribe();
  }

  private getWorkingHours(): void {
    this.loading = true;
    this.restaurant.getWorkingHours(this.restaurantId).pipe(
      finalize(() => this.loading = false),
      untilDestroyed(this),
    ).subscribe((workHours: RestaurantWorkPeriod[]) => {
      this.workingHours = workHours;
    });
  }

  public createPeriod(period: NewRestaurantWorkPeriod): void {
    this.restaurant.createWorkingHoursPeriod(this.restaurantId, period).pipe(
      untilDestroyed(this)
    ).subscribe((response: RestaurantWorkPeriod) => {
      this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.addedPeriod'));
      this.workingHours.push(response);
      this.sortPeriods();
    });
    this.sortPeriods();
  }

  public updatePeriod(period: RestaurantWorkPeriod): void {
    this.restaurant.updateWorkingHoursPeriod(this.restaurantId, period).pipe(
      untilDestroyed(this)
    ).subscribe((response: RestaurantWorkPeriod) => {
      this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.updatePeriod'));
      this.workingHours = this.workingHours.map((p) => {
        return p.id !== response.id ? p : response;
      });
      this.sortPeriods();
    });
  }

  public deletePeriod(id: number): void {
    this.restaurant.deleteWorkingHours(this.restaurantId, id).pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      this.notifier.notify('success', this.translate.instant('pages.restaurantAccount.messages.deletedPeriod'));
      this.workingHours = this.workingHours.filter((p) => p.id !== id);
      this.sortPeriods();
    });
  }

  private sortPeriods(): void {
    this.workingHours.sort((a, b) => {
      if (a.startDay !== b.startDay) {
        return a.startDay - b.startDay;
      }

      return a.startTime.localeCompare(b.startTime);
    });
  }
}
